import {gql} from '@urql/core';

export const PROCUREMENT_PLAN_LIST_QUERY = gql`
  query ProcurementPlanList(
    $sorting: [SortInput!]
    $paging: PaginationInput
  ) {
    procurementPlans(query: { sorting: $sorting, paging: $paging }) {
      nodes {
        id
        title
        status
        createdAt
        updatedAt
        planNumber
        createdBy
        teamId
        teamName
        fiscalYear
        planningPeriod
        totalAmount
        submissionDeadline
        flowCaseId
        flowCaseNumber
        submittedBy
        submissionDate
        templateId
      }
    }
  }
`;

export const PROCUREMENT_PLAN_CREATE_MUTATION = gql`
   mutation CreateProcurementPlan($input: CreateProcurementPlanInput!) {
     createProcurementPlan(input: $input) {
       id
       title
       status
       createdAt
       updatedAt
       planNumber
       createdBy
       templateId
       teamName
       fiscalYear
       planningPeriod
       totalAmount
       submissionDeadline
       approvalType
       steps {
         order
         roleSlug
         stepName
       }
       externalWorkflow
       flowCaseId
       flowCaseNumber
       submittedBy
       submissionDate
       items {
         id
         productName
         productDescription
         productCategory
         categoryType
         quantity
         unit
         unitPrice
         totalPrice
         requiredDate
         quarter
         period
         priority
         preferredSupplierId
         preferredSupplierName
         justification
         tags
       }
     }
   }
`;


export const PROCUREMENT_PLAN_ONE_QUERY = gql`
   query procurementPlan($id: String!) {
     procurementPlan(id: $id) {
      id
      title
      templateId
      status
      planNumber
      createdBy
      teamId
      teamName
      fiscalYear
      planningPeriod
      submissionDeadline
      budgetId
      budgetValidation {
      validatedAt
      validatedBy
      remarks
      totalAmount
    }

    approval {
      approvedBy
      approvedOn
      remarks
    }
      totalAmount
      approvalType
      steps {
        order
        roleSlug
        stepName
      }
      externalWorkflow
      flowCaseId
      flowCaseNumber
      submittedBy
      submissionDate
      items {
        id
        productName
        productDescription
        productCategory
        categoryType
        quantity
        unit
        unitPrice
        totalPrice
        requiredDate
        quarter
        period
        priority
        preferredSupplierId
        preferredSupplierName
        justification
        tags
      }
}
    }
  `;

export const PROCUREMENT_PLAN_UPDATE_MUTATION = gql`
   mutation UpdateProcurementPlan($id: String!, $input: UpdateProcurementPlanInput!) {
     updateProcurementPlan(id: $id, input: $input) {
       id
       title
       status
       planNumber
       submissionDeadline
       createdBy
       teamId
       teamName
       planningPeriod
       budgetId
       totalAmount
       approvalType
       steps {
         order
         roleSlug
         stepName
       }
       externalWorkflow
       flowCaseId
       flowCaseNumber
       submittedBy
       submissionDate
       items {
         id
         productName
         productDescription
         productCategory
         categoryType
         quantity
         unit
         unitPrice
         totalPrice
         requiredDate
         quarter
         period
         priority
         preferredSupplierId
         preferredSupplierName
         justification
         tags
       }
     }
   }
`;

export const PROCUREMENT_PLAN_VALIDATE_BUDGET_MUTATION = gql`
  mutation ValidateBudget($planId: String!, $totalAmount: Float!, $remarks: String) {
    validateBudget(planId: $planId, totalAmount: $totalAmount, remarks: $remarks) {
      id
      status
      budgetValidation {
        validatedAt
        totalAmount
        validatedBy
        remarks
      }
    }
  }
`;

export const PROCUREMENT_PLAN_APPROVE_MUTATION = gql`
  mutation ApprovePlan($planId: String!, $remarks: String) {
    approvePlan(planId: $planId, remarks: $remarks) {
      id
      status
      approval {
        approvedBy
        approvedOn
        remarks
      }
    }
  }
`;


export const PROCUREMENT_PLAN_DELETE_MUTATION = gql`
  mutation DeleteProcurementPlan($id: String!) {
    deleteProcurementPlan(id: $id) {
      id
    }
  }
`;

// TypeScript interfaces for submit mutation
export interface ConflictingPlan {
  id: string;
  planNumber: string;
  status: string;
  teamId: string;
  teamName?: string;
  fiscalYear: number;
  planningPeriod: string;
}

export interface ConflictInfo {
  hasConflict: boolean;
  conflictingPlans?: ConflictingPlan[];
  message?: string;
}

export interface SubmitResponse {
 plan: {
    id: string;
    title: string;
    status: string;
    planNumber: string;
    createdBy: string;
    teamId: string;
    teamName: string;
    fiscalYear: number;
    planningPeriod: string;
    totalAmount: number;
    submissionDeadline: string;
    budgetId: string;
    createdAt: string;
    updatedAt: string;
    approvalType: string;
    steps: Array<{
      order: number;
      roleSlug: string;
      stepName?: string;
    }>;
    externalWorkflow?: Record<string, any>;
    flowCaseId?: string;
    flowCaseNumber?: string;
    submittedBy?: string;
    submissionDate?: string;
    items: Array<{
      id: string;
      productName: string;
      productDescription: string;
      productCategory: string;
      categoryType: string;
      quantity: number;
      unit: string;
      unitPrice: number;
      totalPrice: number;
      requiredDate: string;
      quarter: number;
      period: string;
      priority: string;
      preferredSupplierId: string;
      preferredSupplierName: string;
      justification: string;
      tags: string[];
    }>;
    budgetValidation?: {
      validatedAt: string;
      validatedBy: string;
      remarks: string;
      totalAmount: number;
    };
    approval?: {
      approvedBy: string;
      approvedOn: string;
      remarks: string;
    };
  };
  conflict?: ConflictInfo;
  wasSubmitted: boolean;
  message: string;
}

export const SUBMIT_PROCUREMENT_PLAN = gql`
   mutation SubmitProcurementPlan($input: SubmitProcurementPlanInput!) {
     submitProcurementPlan(input: $input) {
       plan {
         id
         title
         status
         planNumber
         createdBy
         teamId
         teamName
         fiscalYear
         planningPeriod
         totalAmount
         submissionDeadline
         budgetId
         createdAt
         updatedAt
         approvalType
         steps {
           order
           roleSlug
           stepName
         }
         externalWorkflow
         flowCaseId
         flowCaseNumber
         submittedBy
         submissionDate
         items {
           id
           productName
           productDescription
           productCategory
         categoryType
           quantity
           unit
           unitPrice
           totalPrice
           requiredDate
           quarter
           period
           priority
           preferredSupplierId
           preferredSupplierName
           justification
           tags
         }
         budgetValidation {
           validatedAt
           validatedBy
           remarks
           totalAmount
         }
         approval {
           approvedBy
           approvedOn
           remarks
         }
       }
       conflict {
         hasConflict
         conflictingPlans {
           id
           planNumber
           status
           teamId
           fiscalYear
           planningPeriod
         }
         message
       }
       wasSubmitted
       message
     }
   }
`;

export const SUBMIT_PROCUREMENT_PLAN_BY_ID = gql`
     mutation SubmitProcurementPlanById($id: String!) {
       submitProcurementPlan(id: $id) {
         id
         title
         status
         planNumber
         createdBy
         teamId
         teamName
         fiscalYear
         planningPeriod
         totalAmount
         submissionDeadline
         budgetId
         createdAt
         updatedAt
         approvalType
         steps {
           order
           roleSlug
           stepName
         }
         externalWorkflow
         flowCaseId
         flowCaseNumber
         submittedBy
         submissionDate
         items {
           id
           productName
           productDescription
           productCategory
         categoryType
           quantity
           unit
           unitPrice
           totalPrice
           requiredDate
           quarter
           period
           priority
           preferredSupplierId
           preferredSupplierName
           justification
           tags
         }
         budgetValidation {
           validatedAt
           validatedBy
           remarks
           totalAmount
         }
         approval {
           approvedBy
           approvedOn
           remarks
         }
       }
     }
  `;

export const CHECK_PROCUREMENT_PLAN_CONFLICTS = gql`
   query CheckProcurementPlanConflicts(
     $teamId: String!
     $fiscalYear: Float!
     $planningPeriod: String!
     $excludeId: String
   ) {
     checkProcurementPlanConflicts(
       teamId: $teamId
       fiscalYear: $fiscalYear
       planningPeriod: $planningPeriod
       excludeId: $excludeId
     ) {
       hasConflict
       conflictingPlans {
         id
         planNumber
         status
         teamId
         teamName
         fiscalYear
         planningPeriod
       }
       message
     }
   }
`;
